import { useState } from 'react'
import { CheckCircle2, MessageCircle, LayoutDashboard, Loader2 } from 'lucide-react'
import type { ChannelType } from './types'

interface Step5CompleteProps {
  storeName: string
  businessType: string
  channelType: ChannelType
  instanceName: string
  createdChannelId: string | null
  botName: string
  aiEnabled: boolean
  productsCount: number
  faqsCreated?: number
  faqQuestions?: string[]
  onGoToInstances: () => void
  onGoToDashboard: () => void
}

export default function Step5Complete(props: Step5CompleteProps) {
  const [navigating, setNavigating] = useState<'instances' | 'dashboard' | null>(null)

  const handleNavigate = (type: 'instances' | 'dashboard') => {
    if (navigating) return
    setNavigating(type)
    if (type === 'instances') {
      props.onGoToInstances()
    } else {
      props.onGoToDashboard()
    }
  }

  return (
    <div className="flex flex-col items-center gap-8">
      {/* Success illustration */}
      <div className="relative w-full aspect-square max-w-[280px] bg-[#075f55]/5 rounded-full flex items-center justify-center overflow-hidden border border-[#075f55]/10">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#075f55]/10 to-transparent opacity-50" />
        <div className="bg-[#075f55]/10 w-44 h-44 rounded-full flex items-center justify-center animate-pulse">
          <CheckCircle2 className="h-20 w-20 text-[#075f55]" />
        </div>
        {/* Decorative dots */}
        <div className="absolute top-10 right-10 w-4 h-4 bg-[#075f55]/20 rounded-full" />
        <div className="absolute bottom-20 left-10 w-6 h-6 bg-[#075f55]/30 rounded-full" />
      </div>

      {/* Text */}
      <div className="flex flex-col items-center gap-3 text-center">
        <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-tight">
          ¡Todo está listo!
        </h1>
        <p className="text-slate-600 text-base font-normal leading-relaxed max-w-[360px]">
          Tu cuenta de IridiaChat ha sido configurada con éxito. Ya puedes empezar a gestionar
          tus canales o monitorizar el rendimiento de tu equipo.
        </p>
      </div>

      {/* Badge */}
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#075f55]/5 border border-[#075f55]/10 rounded-full text-[#075f55] text-sm font-medium">
        <CheckCircle2 className="h-4 w-4" />
        Configuración completada satisfactoriamente
      </div>

      {/* Activated Capabilities Summary */}
      <div className="w-full bg-slate-50 border border-slate-200/90 rounded-2xl p-5 text-left space-y-3">
        <div className="text-xs font-bold text-slate-900 uppercase tracking-wider">
          Habilidades comerciales activas en tu cuenta:
        </div>
        <div className="space-y-2.5 text-xs text-slate-700">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-[#075f55] shrink-0 mt-0.5" />
            <div>
              <strong className="text-slate-900">Inventario directo y reglas de tu negocio:</strong> Responde existencias y precios respetando las políticas comerciales de tu tienda.
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-[#075f55] shrink-0 mt-0.5" />
            <div>
              <strong className="text-slate-900">Entendimiento visual de fotos:</strong> Identifica imágenes de productos y consultas compartidas en el chat.
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-[#075f55] shrink-0 mt-0.5" />
            <div>
              <strong className="text-slate-900">Transferencia inteligente a tu equipo:</strong> Al confirmar compra, te envía el pedido estructurado para cobrar y despachar.
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp QR warning */}
      {props.channelType === 'WHATSAPP' && (
        <div className="w-full bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
          <div className="flex gap-3">
            <span className="flex-shrink-0 text-xl">⚠️</span>
            <div>
              <h5 className="text-sm font-semibold text-amber-900 mb-1">Importante</h5>
              <p className="text-xs text-amber-800">
                Tu instancia de WhatsApp necesita ser conectada escaneando el código QR.
                Dirígete a <strong>Sesiones WhatsApp</strong> para completar la conexión.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Buttons */}
      <div className="w-full flex flex-col gap-4">
        <button
          onClick={() => handleNavigate('instances')}
          disabled={navigating !== null}
          className="flex min-w-[84px] cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-xl h-14 px-6 bg-[#075f55] text-white text-lg font-bold transition-all hover:bg-[#075f55]/90 active:scale-[0.98] shadow-lg shadow-[#075f55]/20 w-full disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {navigating === 'instances' ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Redirigiendo...</span>
            </>
          ) : (
            <>
              <span>Ir a Canales</span>
              <MessageCircle className="h-5 w-5" />
            </>
          )}
        </button>
        <button
          onClick={() => handleNavigate('dashboard')}
          disabled={navigating !== null}
          className="flex min-w-[84px] cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-xl h-14 px-6 bg-[#075f55]/10 text-[#075f55] text-lg font-bold transition-all hover:bg-[#075f55]/20 active:scale-[0.98] w-full disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {navigating === 'dashboard' ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Redirigiendo...</span>
            </>
          ) : (
            <>
              <span>Ir a Dashboard</span>
              <LayoutDashboard className="h-5 w-5" />
            </>
          )}
        </button>
      </div>
    </div>
  )
}
